import {DialogProps} from '@mui/material'
import {OcTextFieldLineType} from '../BasicOcComponents/OcTextFields'
import {OCSelectOptionType} from '../BasicOcComponents/OcAutocompleteSelect'
import {OcGenericDialogFieldEditorBaseProps} from './OcGenericEditComponents'
import {OcGenericDialogFieldBaseProps} from './OcGenericDialogInterfaces'
import {OcButtonProps} from '../BasicOcComponents/utils'
import {DataCyProps} from '../dataCyProps'

export {type CustomValidationResult, hasValidationError} from './OcGenericEditComponents'

// FieldType = string
export interface OcGenericDialogFieldStringProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, string>,
        | 'index'
        | 'label'
        | 'width'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'validateValue'
        | 'helperText'
        | 'defaultValue'
        | 'placeholder'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.String
    lineType?: OcTextFieldLineType.Single
}

export interface OcGenericDialogFieldStringMultilineProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, string>,
        | 'index'
        | 'label'
        | 'width'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'validateValue'
        | 'helperText'
        | 'defaultValue'
        | 'placeholder'
        | 'maxRows'
        | 'minRows'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.String
    lineType: OcTextFieldLineType.Multi | OcTextFieldLineType.Area
}

// FieldType = number
export interface OcGenericDialogFieldNumberProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, number>,
        | 'index'
        | 'label'
        | 'width'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'validateValue'
        | 'allowDecimal'
        | 'decimalPlaces'
        | 'minValue'
        | 'maxValue'
        | 'changeStep'
        | 'helperText'
        | 'defaultValue'
        | 'placeholder'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.Number
}

// FieldType = enum {label: string, value: string}
export interface OcGenericDialogFieldEnumProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, OCSelectOptionType | OCSelectOptionType[]>,
        | 'index'
        | 'label'
        | 'width'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'validateValue'
        | 'noOptionsMessage'
        | 'helperText'
        | 'multiSelect'
        | 'defaultValue'
        | 'placeholder'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.Enum
    suggestions: OCSelectOptionType[]
    stickyValues?: string[]
}

// FieldType = checkbox - boolean
export interface OcGenericDialogFieldCheckboxProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, boolean>,
        | 'index'
        | 'label'
        | 'width'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'defaultValue'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.Checkbox
    mandatory?: undefined
}

// FieldType = switch - boolean
export interface OcGenericDialogFieldSwitchProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, boolean>,
        | 'index'
        | 'label'
        | 'width'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'defaultValue'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.Switch
    mandatory?: undefined
}

// FieldType radio group - key is string or number
export interface OcGenericDialogFieldRadiogroupProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, string | number>,
        | 'index'
        | 'label'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'defaultValue'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.Radiogroup
    suggestions: OCSelectOptionType[]
}

// FieldType Dropzone group - key is string or number
export interface OcGenericDialogFieldDropzoneProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, File>,
        | 'index'
        | 'label'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'maxValue'
        | 'helperText'
        | 'placeholder'
        | 'validateValue'
        | 'displayCondition'
        | 'defaultValue'
    > {
    type: OcGenericDialogFieldType.Dropzone
    allowedTypes: string[]
}

/**
 * Represents a date as an ISO 8601 string in the format YYYY-MM-DD for date only,
 * or YYYY-MM-DDTHH:mm:ss.sssZ for date with time.
 */
type OcGenericDialogDateString = string

/**
 * Represents date and time in milliseconds as number
 */
type OcGenericDialogDateTimeNumber = number

export interface OcGenericDialogFieldDateTimeProps<SourceType>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, OcGenericDialogDateString | OcGenericDialogDateTimeNumber>,
        | 'index'
        | 'label'
        | 'width'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'validateValue'
        | 'helperText'
        | 'defaultValue'
        | 'placeholder'
        | 'displayCondition'
        | 'minDate'
        | 'maxDate'
        | 'dateOnly'
    > {
    type: OcGenericDialogFieldType.DateTime
}

// FieldType = custom
export interface OcGenericDialogFieldCustomProps<SourceType, FieldType, CustomPropsType extends object>
    extends Pick<
        OcGenericDialogFieldBaseProps<SourceType, FieldType, CustomPropsType>,
        | 'index'
        | 'label'
        | 'width'
        | 'mandatory'
        | 'mandatoryErrorText'
        | 'disabled'
        | 'disableReset'
        | 'getValue'
        | 'setValue'
        | 'validateValue'
        | 'allowDecimal'
        | 'decimalPlaces'
        | 'minValue'
        | 'maxValue'
        | 'changeStep'
        | 'helperText'
        | 'multiSelect'
        | 'defaultValue'
        | 'placeholder'
        | 'combineItemsFunc'
        | 'displayCondition'
    > {
    type: OcGenericDialogFieldType.Custom
    customEditor: {
        additionalProps: CustomPropsType
        editor: React.ComponentType<OcGenericDialogCustomEditorProps<FieldType> & CustomPropsType>
    }
}

export interface OcGenericDialogOutput {
    /** Name of the parent group. */
    groupName: string
    /** Name of the field. */
    fieldName: string
    /** Current value of the field. If changed, the changed value is returned.
     * If in create mode and not changed, default value is returned.
     * If not create mode and not changed, initial value (before edit) is returned. */
    value?: any
    /** Info if the field is changed or not. */
    isChanged: boolean
    isDisabled: boolean
}

// Group and Dialog interfaces
export interface OcGenericDialogGroupProps<SourceType> {
    /** Index used for sorting groups. */
    index: number
    /** Display border at top of the row with group. */
    borderTop?: boolean
    /** Display border at bottom of the row with group. */
    borderBottom?: boolean
    /** If filled, it will be displayed as label in inputs. */
    label?: string
    /** If set to true, group fields can be hidden by clicking on icon next to group label. */
    hideable?: boolean
    /** Dict of fields in the group. */
    fields: {
        [key: string]:
            | OcGenericDialogFieldStringProps<SourceType>
            | OcGenericDialogFieldStringMultilineProps<SourceType>
            | OcGenericDialogFieldNumberProps<SourceType>
            | OcGenericDialogFieldCheckboxProps<SourceType>
            | OcGenericDialogFieldSwitchProps<SourceType>
            | OcGenericDialogFieldRadiogroupProps<SourceType>
            | OcGenericDialogFieldEnumProps<SourceType>
            | OcGenericDialogFieldDateTimeProps<SourceType>
            | OcGenericDialogFieldCustomProps<SourceType, any, any>
            | OcGenericDialogFieldDropzoneProps<SourceType>
    }
}

export enum OcGenericDialogStatus {
    Active = 0,
    Deactivated = 1,
    Deleted = 2
}

export interface OcGenericDialogTitleProps {
    /** Initial status of the data. Default status is 'undefined' */
    initialStatus?: OcGenericDialogStatus
    /** Components for first row of the dialog title. */
    firstRow?: {
        /** Component displayed in left part of first row, aligned to left side. */
        left?: React.ReactNode
        /** Component displayed in middle part of first row, centered. */
        middle?: React.ReactNode
        /** Component displayed in right part of first row, aligned to right side. */
        right?: React.ReactNode
    }
    /** Components for second row of the dialog title. */
    secondRow?: {
        /** Component displayed in left part of second row, aligned to left side. */
        left?: React.ReactNode
        /** Properties for status component, displayed on right side of the second row. Not allowed in create mode. */
        status?: {
            /** Text to display next to status controls */
            label: string
            /** Delete icon is displayed if true */
            deletable: boolean
            /** Switch for activation/deactivation is displayed if true */
            activable: boolean
            /** Disable activation/deactivation switch */
            disableSwitch?: boolean
            /** Disable delete icon */
            disableDelete?: boolean
            /** Save will not fail with errors in deleted state */
            deletedAllowSaveError?: boolean
            /** Save will not fail with errors in deactivated state */
            deactivatedAllowSaveError?: boolean
        }
    }
}

export enum OcGenericDialogFieldType {
    String = 'string',
    Number = 'number',
    Enum = 'enum',
    Checkbox = 'checkbox',
    Switch = 'switch',
    Radiogroup = 'radiogroup',
    DateTime = 'datetime-local',
    Custom = 'custom',
    Dropzone = 'dropzone'
}

export interface OcGenericDialogAction {
    /** Label for the button. */
    label: string
    /** Disable button when dialog has validation errors. */
    disableOnError?: boolean
    /** Disable button when nothing changed in dialog. */
    disableWithoutChange?: boolean
    /** Execute onClose even with validation errors. */
    closeWithErrors?: boolean
    /** Run validation before onClose. */
    validation?: boolean
    /** If true, dialog will apply changes via 'setValue' method. */
    applyChanges?: boolean
    /** Basic props for OcButton, color, variant, style,.. */
    buttonBaseProps?: Omit<OcButtonProps, 'onClick' | 'children'>
    /** If true, validation will not be executed if status = 'deleted' */
    skipValidationForDeleted?: boolean
}

export interface OcGenericDialogProps<SourceType> extends Omit<DialogProps, 'onClose'>, DataCyProps {
    /** Data to work work with.
     * For create mode use empty array
     * For bulk edit mode use array with more than one item
     * For single edit use array with one item
     */
    data: SourceType[]
    open: boolean
    /** Function to execute when one of action buttons is activated. In the end it should close dialog.
     * In create mode it should create the final object from fields parameter.
     * In single and bulk edit mode the objects are already changed.
     * @param actionKey 'key' of the action from 'actions' property
     * @param status curent status of the data object (active, deactivated, deleted)
     * @param fields array of all fields with curent state (value, isChanged,...)
     */
    onClose: (
        actionKey: string,
        fields: OcGenericDialogOutput[],
        status?: OcGenericDialogStatus
    ) => Promise<void> | void
    /** Dictionary of groups for the dialog. */
    dataGroups: {[key: string]: OcGenericDialogGroupProps<SourceType>}
    /** Dictionary of actions for the dialog.
     * OcButton is used as component for the action.
     */
    actions?: {
        [key: string]: OcGenericDialogAction
    }
    /** Custom title component to override the default. */
    customDialogTitle?: JSX.Element
    /** Properties to set up the default title component */
    defaultDialogTitleProps?: OcGenericDialogTitleProps
}

export type OcGenericDialogCustomEditorProps<FieldType> = Pick<
    OcGenericDialogFieldEditorBaseProps<FieldType>,
    | 'getValue'
    | 'changedValue'
    | 'isChanged'
    | 'disabled'
    | 'onChange'
    | 'customValidationResult'
    | 'errorMessage'
    | 'createMode'
    | 'mandatory'
    | 'fieldName'
    | 'defaultValue'
>
